/* eslint-disable no-useless-escape */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LOOKUP_TYPES, ROUTES, ERRORS, NOTIFICATION_TYPES, UPLOAD_PRELOADER_STATES, SCAN_STATES, PAID_ONLY_HEADERS } from '@mdc/constants';
import { ScanInput, ConfirmModal, UserContext } from '@mdc/ui';
import { withUploadPreloaderProvider, FileScanService, Base64, localStorage, logService, Authenticator } from '@mdc/services';
import { navigate } from 'gatsby';
import Cookies from 'js-cookie';
import { COOKIE_ALREADY_USED } from '@mdc/constants';

import './ScanComponent.scss';

const LAST_UPLOAD_ID = 'lastUploadId';

const ScanComponent = (props) => {
    const { t, ready } = useTranslation();
    const { notify, onFileDrop, uploadProvider, showText, scanError, isFocused } = props;
    const userContext = useContext(UserContext);
    const isError = useMemo(() => scanError?.status && scanError?.status?.toString().match(/^(?:5)\d+$/) ? true : false, [scanError]);
    const [windowLocation, setWindowLocation] = useState({ href: '/' });
    const [showRestrictedModal, setShowRestrictedModal] = useState(false);
    const [restrictedOptions, setRestrictedOptions] = useState(() => {
        // eslint-disable-next-line camelcase
        if (userContext.state === userContext.STATES.LOGGED_IN && userContext.data?.paid_user === 1) {
            return [];
        }
        return PAID_ONLY_HEADERS;
    });

    const isUserLogin = useMemo(() => userContext.state === userContext.STATES.LOGGED_IN, [userContext]);

    // eslint-disable-next-line camelcase
    const maximumFileSize = useMemo(() => userContext.state === userContext.STATES.LOGGED_IN && userContext.data?.max_upload_file_size, [userContext.state]);

    useEffect(() => {
        setWindowLocation(window && window.location);
    }, []);

    useEffect(() => {
        // eslint-disable-next-line camelcase
        if (userContext.state === userContext.STATES.LOGGED_IN && userContext.data?.paid_user === 1) {
            setRestrictedOptions([]);
        } else {
            setRestrictedOptions(PAID_ONLY_HEADERS);
        }
    }, userContext.state);

    const privateScanningMessage = useMemo(() => {
        if (userContext.state === userContext.STATES.LOGGED_IN) {
            return {
                body: <p>{t('To enable private processing, please upgrade your license')}</p>,
                acceptText: 'Upgrade your license'
            };
        }

        return {
            body: <p>{t('Private processing is only available for Commercial and Enterprise users')}</p>,
            acceptText: 'Sign in'
        };
    }, [userContext]);

    const handleOnFileUpload = useMemo(() => (files) => {
        if (!files?.length) {
            return;
        }

        const fileScanService = new FileScanService(files[0], maximumFileSize);

        fileScanService.onError((error, headers) => {
            if (error === ERRORS.FILE_SIZE_EXCEEDED) {
                notify({
                    message: t('Please select a file less than {{maxSize}}MB', { maxSize: maximumFileSize || '140' }),
                    type: NOTIFICATION_TYPES.WARNING
                });
            } else if (error === ERRORS.NETWORK_ERROR) {
                notify({
                    message: t('Network error occurred, and your request could not be completed. Please refresh the page'),
                    type: NOTIFICATION_TYPES.CRITICAL
                });
            } else if (error === ERRORS.FILE_EMPTY) {
                notify({
                    message: t('The file you selected is empty'),
                    type: NOTIFICATION_TYPES.CRITICAL
                });
            } else if (error === ERRORS.LIMIT_ERROR) {
                if (headers) {
                    const limitFor = headers['x-ratelimit-for'];
                    const limit = headers['x-ratelimit-limit'];
                    let limitForText;
                    switch (limitFor) {
                        case 'sandbox_api':
                            limitForText = 'Analysis API';
                            break;
                        case 'reputation_api':
                            limitForText = 'Reputation API';
                            break;
                        case 'prevention_api':
                            limitForText = 'Prevention API';
                            break;
                        default:
                            // Log error when there is no limitFor define
                            logService.error('Undefined x-ratelimit-for header', limitFor);
                            break;
                    }

                    if (isUserLogin) {
                        // If user logged in
                        if (limitForText) {
                            notify({
                                // eslint-disable-next-line no-useless-escape
                                message: t('You have reached your account limit of ({{limit}} requests/day for the {{limitFor}}). Please go to our <a href=\"/store\">online store</a> to extend your limits or contact our <a href=\"https://www.opswat.com/contact\">sales team</a>', { limitFor: limitForText, limit: limit }),
                                type: NOTIFICATION_TYPES.CRITICAL
                            });
                        } else {
                            notify({
                                // eslint-disable-next-line no-useless-escape
                                message: t('You have reached your account limit of ({{limit}} requests/day). Please go to our <a href=\"/store\">online store</a> to extend your limits or contact our <a href=\"https://www.opswat.com/contact\">sales team</a>', { limit: limit }),
                                type: NOTIFICATION_TYPES.CRITICAL
                            });
                        }
                    } else {
                        // If user didn't log in yet
                        if (limitForText) {
                            notify({
                                // eslint-disable-next-line no-useless-escape
                                message: t('You have used up your limit of ({{limit}} requests/day for the {{limitFor}}). <a href=\"/login\">Log in</a> or <a href=\"/login\">register</a> to get access more requests per day, no credit card required!', { limitFor: limitForText, limit: limit }),
                                type: NOTIFICATION_TYPES.CRITICAL
                            });
                        } else {
                            notify({
                                // eslint-disable-next-line no-useless-escape
                                message: t('You have used up your limit of ({{limit}} requests/day). <a href=\"/login\">Log in</a> or <a href=\"/login\">register</a> to get access more requests per day, no credit card required!', { limit: limit }),
                                type: NOTIFICATION_TYPES.CRITICAL
                            });
                        }
                    }
                } else {
                    notify({
                        message: t('Rate limit exceeded, retry after the limit is reset'), // we may need to update this one not to be too general?
                        type: NOTIFICATION_TYPES.CRITICAL
                    });
                }
            } else {
                notify({
                    message: error,
                    type: NOTIFICATION_TYPES.CRITICAL
                });
            }
            uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.INACTIVE);
        });
        uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.IN_PROGRESS);
        fileScanService.scan();

        fileScanService.onProgress((status, { hash, percent, data_id }) => {
            switch (status) {
                case SCAN_STATES.CALCULATE_HASH:
                    uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.IN_PROGRESS);
                    uploadProvider.setPreloaderText(t('Calculating the hash'));
                    break;
                case SCAN_STATES.SEARCH_HASH:
                    uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.IN_PROGRESS);
                    uploadProvider.setPreloaderText(t('Checking for existing results'));
                    break;
                case SCAN_STATES.FOUND_HASH:
                    uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.IN_PROGRESS);
                    uploadProvider.setPreloaderText(t('Found results'));

                    window && window.setTimeout(() => {
                        uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.INACTIVE);
                        uploadProvider.setPreloaderText('');
                    }, 1500);
                    break;
                case SCAN_STATES.UPLOAD:
                    uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.IN_PROGRESS);
                    uploadProvider.setPreloaderText(t('Uploading file ({{percent}}%)', { percent }));
                    break;
                case SCAN_STATES.UPLOAD_COMPLETE:
                    uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.IN_PROGRESS);
                    uploadProvider.setPreloaderText(t('Successfully uploaded'));

                    if (data_id) {
                        localStorage.setItem(LAST_UPLOAD_ID, data_id);
                    }
                    window && window.setTimeout(() => {
                        uploadProvider.setPreloaderState(UPLOAD_PRELOADER_STATES.INACTIVE);
                        uploadProvider.setPreloaderText('');
                    }, 1500);
                    break;
                default:
            }

            if (hash) {
                navigate(`${ROUTES.results.href}/file/${hash}/hash/overview`);
            }
            if (data_id) {
                navigate(`${ROUTES.results.href}/file/${data_id}/regular/overview`);
            }

            if (hash || data_id) {
                Cookies.set(COOKIE_ALREADY_USED, true, { secure: true });
            }
        });
    }, [maximumFileSize, isUserLogin]);

    useEffect(() => {
        onFileDrop(handleOnFileUpload, 'handleOnFileUpload_callback_Id');
    }, [handleOnFileUpload]);

    const onProcess = (lookupType, term) => {
        let encodedTerm;
        if (!lookupType) {
            return;
        }

        Cookies.set(COOKIE_ALREADY_USED, true, { secure: true });

        switch (lookupType) {
            case LOOKUP_TYPES.CVE:
                navigate(`${ROUTES.vulnerabilities}/${term}`, { state: { isError } });
                break;
            case LOOKUP_TYPES.DOMAIN:
                encodedTerm = Base64.encode(term.replace(/\/$/, ''));
                navigate(`${ROUTES.results.href}${ROUTES.results.domain}/${encodedTerm}`, { state: { isError } });
                break;
            case LOOKUP_TYPES.URL:
                encodedTerm = Base64.encode(term.replace(/\/$/, ''));
                navigate(`${ROUTES.results.href}${ROUTES.results.url}/${encodedTerm}`, { state: { isError } });
                break;
            case LOOKUP_TYPES.HASH:
                navigate(`${ROUTES.results.href}${ROUTES.results.file}/${term}/hash/overview`, { state: { isError } });
                break;
            case LOOKUP_TYPES.IP:
                encodedTerm = Base64.encode(term);
                navigate(`${ROUTES.results.href}${ROUTES.results.ip}/${encodedTerm}`, { state: { isError } });
                break;
            default:
                encodedTerm = Base64.encode(term);
                windowLocation.href = '/';
        }
    };

    const onProcessError = (error) => {
        if (error === ERRORS.INVALID_SEARCH_TERM) {
            notify({
                message: t('Invalid search term. Please check to make sure you entered the correct information'),
                type: NOTIFICATION_TYPES.WARNING
            });
        }

        if (error === ERRORS.INVALID_IP) {
            notify({
                message: t('Please enter a valid public IP address'),
                type: NOTIFICATION_TYPES.WARNING
            });
        }
    };

    const placeholder = props.placeholder ? props.placeholder : t('File, URL, IP address, Domain, Hash, or CVE');
    const onFileUpload = props.onFileUpload ? props.onFileUpload : handleOnFileUpload;
    const onProcessCallback = props.onProcess ? props.onProcess : onProcess;
    const onProcessErrorCallback = props.onProcessError ? props.onProcessError : onProcessError;

    const trustNoText = showText ? (
        <h2 className="trustNoText">
            {t('Trust no')}
        </h2>
    ) : null;

    const trustNoTextSm = showText ? (
        <div className="trustNoTextSm">
            <p dangerouslySetInnerHTML={{ __html: t('Trust No File.<br/>Trust No Device') }}/>
        </div>
    ) : null;

    const onRestrictedOptionSelected = () => {
        setShowRestrictedModal(true);
    };

    const onBuy = () => {
        if (!userContext) {
            return;
        }

        if (userContext.state === userContext.STATES.LOGGED_IN) {
            navigate('/store');
        } else {
            Authenticator.authRedirect('/login');
        }
        setShowRestrictedModal(false);
    };

    const onCloseModal = () => {
        setShowRestrictedModal(false);
    };

    if (!ready) {
        return null;
    }

    return (
        <div className="scanInputComponent">
            {trustNoTextSm}
            <div className="scanInputWrapper">
                {trustNoText}
                <div className="processWrapper">
                    <ScanInput
                        advancedOptionsList={props.advancedOptionsList}
                        onFileDrop={onFileDrop}
                        onFileUpload={onFileUpload}
                        onProcess={onProcessCallback}
                        onProcessError={onProcessErrorCallback}
                        placeholder={placeholder}
                        showText={props.showText}
                        restrictedOptions={restrictedOptions}
                        onRestrictedPropSelected={onRestrictedOptionSelected}
                        isFocused={isFocused}
                    />
                </div>
            </div>
            <ConfirmModal
                className="advancedOptionsModal"
                show={showRestrictedModal}
                onAccept={onBuy}
                onCancel={onCloseModal}
                acceptVariant="primary"
                {...privateScanningMessage}
            />
        </div>
    );
};

export default withUploadPreloaderProvider(ScanComponent);

ScanComponent.defaultProps = {
    showText: true
};

ScanComponent.propTypes = {
    advancedOptionsList: PropTypes.array,
    notify: PropTypes.func.isRequired,
    onFileDrop: PropTypes.func.isRequired,
    onFileUpload: PropTypes.func,
    onProcess: PropTypes.func,
    onProcessError: PropTypes.func,
    placeholder: PropTypes.string,
    showText: PropTypes.bool,
    uploadProvider: PropTypes.any,
    scanError: PropTypes.any,
    isFocused: PropTypes.bool
};
